import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Keyboard } from 'lucide-react';

/**
 * Keyboard shortcuts component for enhanced accessibility
 * Implements common keyboard navigation patterns
 */
const KeyboardShortcuts = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Ctrl/Cmd is pressed
      const isMod = e.ctrlKey || e.metaKey;

      // Show keyboard shortcuts dialog (? key)
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      // Don't trigger shortcuts when typing in input fields
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Ctrl/Cmd + Shift + D to toggle theme
      if (isMod && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        setTheme(theme === 'dark' ? 'light' : 'dark');
        return;
      }

      // Keyboard shortcuts with modifier key
      if (isMod) {
        switch (e.key.toLowerCase()) {
          case 'k':
            e.preventDefault();
            // Focus search input if on jobs page
            const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
            if (searchInput) searchInput.focus();
            break;
          case 'h':
            e.preventDefault();
            navigate('/');
            break;
          case 'j':
            e.preventDefault();
            navigate('/jobs');
            break;
          case 'c':
            e.preventDefault();
            navigate('/contact');
            break;
          default:
            break;
        }
      }

      // Escape key to close dialogs
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, theme, setTheme]);

  const shortcuts = [
    { keys: ['?'], description: 'Show this keyboard shortcuts dialog' },
    { keys: ['Ctrl/Cmd', 'Shift', 'D'], description: 'Toggle light/dark theme' },
    { keys: ['Ctrl/Cmd', 'K'], description: 'Focus search bar' },
    { keys: ['Ctrl/Cmd', 'H'], description: 'Go to home page' },
    { keys: ['Ctrl/Cmd', 'J'], description: 'Go to jobs page' },
    { keys: ['Ctrl/Cmd', 'C'], description: 'Go to contact page' },
    { keys: ['Tab'], description: 'Navigate to next interactive element' },
    { keys: ['Shift', 'Tab'], description: 'Navigate to previous element' },
    { keys: ['Enter'], description: 'Activate focused element' },
    { keys: ['Esc'], description: 'Close dialogs and menus' },
  ];

  return (
    <>
      {/* Floating keyboard shortcuts button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-40 rounded-full bg-primary p-3 text-primary-foreground shadow-lg transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Show keyboard shortcuts"
        title="Keyboard shortcuts (?)"
      >
        <Keyboard className="h-5 w-5" />
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
            <DialogDescription>
              Navigate the site faster with these keyboard shortcuts
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-3">
            {shortcuts.map((shortcut, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-lg border border-border p-3 hover:bg-muted/50"
              >
                <span className="text-sm text-muted-foreground">
                  {shortcut.description}
                </span>
                <div className="flex gap-1">
                  {shortcut.keys.map((key, keyIndex) => (
                    <kbd
                      key={keyIndex}
                      className="rounded border border-border bg-muted px-2 py-1 text-xs font-semibold text-foreground"
                    >
                      {key}
                    </kbd>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Screen Reader Users</p>
            <p className="mt-1">
              Use "Skip to main content" link at the top of each page to bypass navigation.
              All forms include proper labels and error messages.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KeyboardShortcuts;
